import { apiClient } from '../lib/apiClient';

/** 'user' = own data (/api/v1/analytics), 'admin' = whole database (/api/v1/admin/analytics). */
export type AnalyticsScope = 'user' | 'admin';

export const CHART_TYPES = ['table', 'bar', 'line', 'area', 'pie', 'scatter'] as const;
export type ChartType = (typeof CHART_TYPES)[number];

export interface ChartSpec {
  readonly type: ChartType;
  readonly x?: string | null;
  readonly y?: readonly string[];
}

export type ColumnKind = 'number' | 'date' | 'text' | 'boolean' | 'json';

export interface AnalyticsColumn {
  readonly name: string;
  readonly kind: ColumnKind;
}

export interface AnalyticsResult {
  readonly columns: readonly AnalyticsColumn[];
  readonly rows: ReadonlyArray<ReadonlyArray<unknown>>;
  readonly rowCount: number;
  readonly truncated: boolean;
  readonly durationMs: number;
}

export interface AnalyticsAnswer {
  readonly title: string;
  readonly explanation: string;
  readonly sql: string;
  readonly chart: ChartSpec;
  readonly result: AnalyticsResult | null;
  readonly error: { readonly code: string; readonly detail: string } | null;
}

export interface SavedQuery {
  readonly id: string;
  readonly title: string;
  readonly question: string | null;
  readonly sql: string;
  readonly chart: ChartSpec;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface SaveQueryInput {
  readonly title: string;
  readonly question: string | null;
  readonly sql: string;
  readonly chart: ChartSpec;
}

function base(scope: AnalyticsScope): string {
  return scope === 'admin' ? '/api/v1/admin/analytics' : '/api/v1/analytics';
}

export const savedQueriesKey = (scope: AnalyticsScope) => ['analyticsSaved', scope] as const;

export async function askQuestion(scope: AnalyticsScope, question: string): Promise<AnalyticsAnswer> {
  const json = await apiClient.post<{ data: AnalyticsAnswer }>(`${base(scope)}/ask`, { question });
  return json.data;
}

export async function runSql(scope: AnalyticsScope, sql: string): Promise<{ sql: string; result: AnalyticsResult }> {
  const json = await apiClient.post<{ data: { sql: string; result: AnalyticsResult } }>(`${base(scope)}/run`, { sql });
  return json.data;
}

export async function listSavedQueries(scope: AnalyticsScope): Promise<SavedQuery[]> {
  const json = await apiClient.get<{ data: SavedQuery[] }>(`${base(scope)}/saved`);
  return json.data;
}

export async function saveQuery(scope: AnalyticsScope, input: SaveQueryInput): Promise<SavedQuery> {
  const json = await apiClient.post<{ data: SavedQuery }>(`${base(scope)}/saved`, input);
  return json.data;
}

export async function runSavedQuery(scope: AnalyticsScope, id: string): Promise<{ sql: string; result: AnalyticsResult }> {
  const json = await apiClient.post<{ data: { sql: string; result: AnalyticsResult } }>(`${base(scope)}/saved/${id}/run`);
  return json.data;
}

export async function deleteSavedQuery(scope: AnalyticsScope, id: string): Promise<void> {
  await apiClient.delete(`${base(scope)}/saved/${id}`);
}
